import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getApiUrl } from '../config/api';
import Header from './Header';
import Footer from './Footer';
import './TermsAndConditions.css';

const TermsAndConditions = ({ onHomeClick, onCategoriesClick, onHowItWorksClick }) => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [content, setContent] = useState('');
  const [title, setTitle] = useState('Terms and Conditions');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Create navigation functions
  const handleHomeClick = () => navigate('/');
  const handleCategoriesClick = () => navigate('/#categories');
  const handleHowItWorksClick = () => navigate('/#how-it-works');
  const handleLoginClick = () => navigate('/login');
  const handleSignupClick = () => navigate('/signup');
  const handleProfileClick = () => navigate('/profile');
  const handleAccountSettingsClick = () => navigate('/settings');

  useEffect(() => {
    fetchTermsAndConditions();
  }, []);

  const fetchTermsAndConditions = async () => {
    try {
      setLoading(true);
      const response = await fetch(getApiUrl('/settings/terms_and_conditions'));
      
      if (!response.ok) {
        throw new Error('Failed to fetch terms and conditions');
      }
      
      const data = await response.json();
      
      if (data.success) {
        setContent(data.data.content);
        setTitle(data.data.title);
      } else {
        throw new Error(data.message || 'Failed to load terms and conditions');
      }
    } catch (err) {
      console.error('Error fetching terms and conditions:', err);
      setError(err.message);
      // Fallback content
      setContent(`# Terms and Conditions

## 1. Acceptance of Terms
By accessing and using Remote Birdy, you accept and agree to be bound by the terms and provision of this agreement.

## 2. Description of Service
Remote Birdy is a marketplace platform that allows users to post and browse classified advertisements, connect buyers and sellers, and access various categories of products and services.

## 3. User Accounts
- You must provide accurate and complete information when creating an account
- You are responsible for maintaining the confidentiality of your account credentials
- You must be at least 18 years old to create an account

## 4. User Conduct
You agree not to post false, misleading, or fraudulent content, violate any applicable laws, or use the platform for illegal activities.

## 5. Privacy and Data Protection
Your privacy is important to us. Please review our Privacy Policy to understand how we collect, use, and protect your information.

## 6. Contact Information
For questions about these Terms and Conditions, please contact us at legal@ravenhub.com

*Last updated: ${new Date().toLocaleDateString()}*`);
    } finally {
      setLoading(false);
    }
  };

  const renderMarkdown = (text) => {
    if (!text) return '';
    
    return text
      .split('\n')
      .map((line, index) => {
        // Handle headers
        if (line.startsWith('# ')) {
          return <h1 key={index} className="terms-h1">{line.substring(2)}</h1>;
        }
        if (line.startsWith('## ')) {
          return <h2 key={index} className="terms-h2">{line.substring(3)}</h2>;
        }
        if (line.startsWith('### ')) {
          return <h3 key={index} className="terms-h3">{line.substring(4)}</h3>;
        }
        
        // Handle lists
        if (line.startsWith('- ')) {
          return <li key={index} className="terms-li">{line.substring(2)}</li>;
        }
        
        // Handle italic text
        if (line.startsWith('*') && line.endsWith('*')) {
          return <p key={index} className="terms-italic">{line.substring(1, line.length - 1)}</p>;
        }
        
        // Handle regular paragraphs
        if (line.trim() !== '') {
          return <p key={index} className="terms-p">{line}</p>;
        }
        
        return <br key={index} />;
      });
  };

  if (loading) {
    return (
      <div className="terms-and-conditions-page">
        <Header 
          onLoginClick={handleLoginClick}
          onSignupClick={handleSignupClick}
          isLoggedIn={isAuthenticated}
          user={user}
          onHomeClick={handleHomeClick}
          onCategoriesClick={handleCategoriesClick}
          onHowItWorksClick={handleHowItWorksClick}
          onProfileClick={handleProfileClick}
          onAccountSettingsClick={handleAccountSettingsClick}
          currentPage="terms-and-conditions"
        />
        <div className="terms-container">
          <div className="terms-loading">
            <div className="spinner"></div>
            <p>Loading Terms and Conditions...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="terms-and-conditions-page">
      <Header 
        onLoginClick={handleLoginClick}
        onSignupClick={handleSignupClick}
        isLoggedIn={isAuthenticated}
        user={user}
        onHomeClick={handleHomeClick}
        onCategoriesClick={handleCategoriesClick}
        onHowItWorksClick={handleHowItWorksClick}
        onProfileClick={handleProfileClick}
        currentPage="terms-and-conditions"
      />
      
      <div className="terms-container">
        <div className="terms-header">
          <h1 className="terms-title">{title}</h1>
          <div className="terms-breadcrumb">
            <span onClick={handleHomeClick} className="breadcrumb-link">Home</span>
            <span className="breadcrumb-separator">/</span>
            <span className="breadcrumb-current">Terms and Conditions</span>
          </div>
        </div>

        <div className="terms-content">
          {error && (
            <div className="terms-error">
              <p>⚠️ {error}</p>
              <p>Showing fallback content.</p>
            </div>
          )}
          
          <div className="terms-text">
            {renderMarkdown(content)}
          </div>
        </div>

        <div className="terms-footer">
          <div className="terms-actions">
            <button 
              onClick={handleHomeClick}
              className="btn btn-primary"
            >
              Back to Home
            </button>
            <button 
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              className="btn btn-secondary"
            >
              Back to Top
            </button>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default TermsAndConditions;
